import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import EventTypeSelection from './EventTypeSelection';
import EventDetailsTab from './EventDetailsTab';
import EventTicketsTab from './EventTicketsTab';
import EventSettingsTab from './EventSettingsTab';
import EventOtherDetailsTab from './EventOtherDetailsTab';
import { CountryData, EventFormData, VenueData } from './types';

import './CreateNewEventV2.css';
import { createEventDraft, createQuickEventV2, getAllCountries, getEventDraftById, updateEventBySlug, activateEventBySlug, getAllCategories } from '../../../api';
import { Spinner } from 'reactstrap';
import Background from '../../Login/Background';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import { Sparkles, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';



const EditQuickEventV2: React.FC = () => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  
  // Force TypeScript recompilation - temporary fix
  const tempFix = true;
  
  // State for event type selection
  const [eventTypeSelected, setEventTypeSelected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEventData, setIsLoadingEventData] = useState(true);
  const [slugId, setSlugId] = useState<string | null>(null);

  // State for active tab
  const [activeTab, setActiveTab] = useState(1);
  const [countries, setCountries] = useState<CountryData[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [error, setError] = useState<string>('');
  // State for validation
  const [tabValidation, setTabValidation] = useState({
    detailsTab: false,
    ticketsTab: false,
    settingsTab: false,
    otherDetailsTab: true // Optional tab, always valid
  });
  
  // State for form data
  const [formData, setFormData] = useState<EventFormData>({
    // Details tab
    eventName: '',
    categories: [],
    eventDate: '',
    endDate: '',
    eventTime: '10:00',
    eventEndTime: '10:00',
    isSingleDayEvent: false,
    duration: '',
    eventImage: null,
    eventDescription: '',
    shortDescription: '',
    eventLocation: '',
    venue: '',
    venueDetails: {
      venueId: '',
      venueName: '',
      venueAddress: '',
      eventSlug: '',
      createdAt: '',
      updatedAt: '',
      eventDate: '',
      status: 'active',
      id: '',
      name: '',
      addressLine1: '',
      addressLine2: '',
      country: '',
      state: '',
      city: '',
      zipCode: '',
      latitude: undefined,
      longitude: undefined,
      formattedAddress: '',
    } as VenueData,
    isOnlineEvent: false, // Default to offline event
    hasMultipleVenues: false,
    venues: [],
    
    // Tickets tab
    tickets: [],
    hasMultipleTicketGroups: false,
    ticketGroups: [],
    isTicketFree: false, // Default to paid tickets
    absorbFees: false,

    // Settings tab
    showOtherFees: true,
    isOtherAttendeeRequired: false,
    hasTermsAndConditions:false,
    termsAndConditions:'',
    templateType:'',
    
    // Other Details tab
    agenda: [],
    lineup: [],
    sponsors: [],
  });

    // helper to normalize any backend shape → boolean
const toAbsorbBool = (v: any) => {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const s = v.toLowerCase();
    if (s === 'true' || s === 'organizer') return true;
    if (s === 'false' || s === 'attendee') return false;
  }
  return false;
};

  useEffect(() => {
    const loadEventData = async () => {
      if (!eventId) {
        setIsLoadingEventData(false);
        return;
      }

      try {
        setIsLoadingEventData(true);
        const response = await getEventDraftById(eventId);
        console.log('Loading event data for edit:', response);

        // Preview "Edit Event" is used for draft + live/inactive/closed.
        // Previously only draft was allowed, so non-draft fell through to the
        // Create New Event type picker even though the URL was an edit route.
        const status = String(response?.data?.event?.eventStatus || '').toLowerCase();
        const editableStatuses = ['draft', 'active', 'inactive', 'closed'];
        if (response?.data?.event && editableStatuses.includes(status)) {
          const eventData = response.data;
          const event = eventData.event;

          const normalizeTickets = (list: any[] = []) =>
            list
              .map((t) => {
                const isGroup = !!(
                  t.isGroupTicket === true ||
                  t.isGroupTicket === 'true' ||
                  Number(t.isGroupTicket) === 1 ||
                  t.groupTicketCount ||
                  t.type === 'group'
                );
                return {
                ...t,
                // include ticketType from backend!
                name: (
                  t?.ticketType ??
                  t?.name ??
                  t?.ticketName ??
                  t?.title ??
                  t?.type ??
                  ""
                )
                  .toString()
                  .trim(),
                  isGroupTicket: isGroup,
                  type: isGroup ? 'group' : 'single',
                groupingId: t.groupingId || t.ticketGroupId || "",
                ticketGroupId: t.ticketGroupId || t.groupingId || "",
                };
              })
              .filter((t) => !!t.name); // drop truly empty
          const normalizedTickets = normalizeTickets(eventData.ticketTypes);

          const normalizedGroups = Array.isArray(eventData.ticketGroups)
            ? eventData.ticketGroups
            : [];
          const hasGroups = normalizedGroups.length > 0;

          const firstVenue = eventData.venues?.[0] || (Array.isArray(event.locationData) ? event.locationData[0] : null) || {};

          // Populate form data with existing event data
          setFormData({
            // Details tab
            eventName: event.eventName || "",
            categories: event.categoryId ? [String(event.categoryId)] : [],
            eventDate: event.eventDate || "",
            endDate: event.endDate || "",
            eventTime: event.eventTime || "10:00",
            eventEndTime: event.endTime || "10:00",
            isSingleDayEvent: event.isSameDayEvent === true || event.isSameDayEvent === 'true' || event.isSameDayEvent === 1 || !event.endDate || event.eventDate === event.endDate,
            duration: event.eventDuration || "",
            eventImage: event.bannerImgDesktop
              ? { name: "existing-image", url: event.bannerImgDesktop }
              : null,
            eventDescription: event.eventDescription || "",
            shortDescription: event.shortDescription || "",
            eventLocation: event.eventLocation || "",
            venue: event.eventVenue || "",
            venueDetails: {
              venueId: firstVenue.venueId || "",
              venueName: firstVenue.venueName || event.eventVenue || firstVenue.name || "",
              venueAddress: event.eventLocation || firstVenue.addressLine1 || "",
              eventSlug: event.slugId || "",
              createdAt: event.createdAt || "",
              updatedAt: event.updatedAt || "",
              eventDate: event.eventDate || "",
              status: "active",
              id: firstVenue.id || "",
              name: event.eventVenue || firstVenue.name || "",
              addressLine1: event.eventAddress1 || firstVenue.addressLine1 || "",
              addressLine2: event.eventAddress2 || firstVenue.addressLine2 || "",
              country: event.eventCountry || firstVenue.country || "",
              state: event.eventState || firstVenue.state || "",
              city: event.eventCity || firstVenue.city || "",
              zipCode: event.eventZip || firstVenue.zipCode || "",
              latitude: firstVenue.latitude || 0,
              longitude: firstVenue.longitude || 0,
              formattedAddress: event.eventLocation || firstVenue.formattedAddress || "",
            } as VenueData,
            isOnlineEvent: event.eventType === "online",
            hasMultipleVenues: event.hasMultipleVenues || false,
            venues: eventData.venues || [],
            tickets: normalizedTickets,
            hasMultipleTicketGroups: Boolean(
              event.hasMultipleTicketGroups && hasGroups
            ),
            ticketGroups: normalizedGroups,
            isTicketFree: event.ticketType === "free",
            absorbFees: toAbsorbBool(event.absorbFees),
            showOtherFees: event.showOtherFees || false,
            isOtherAttendeeRequired: event.isOtherAttendeeRequired || false,
            hasTermsAndConditions: event.hasTermsAndConditions || false,
            termsAndConditions: event.termsAndConditions || "",
            templateType: event.templateName || "corporate",
            agenda: eventData.agendas || [],
            lineup: eventData.lineups || [],
            sponsors: eventData.sponsors || [],
          });

          setSlugId(event.slugId || null);
          setEventTypeSelected(true);
          setError('');
        } else {
          setError(
            response?.data?.event
              ? `This event cannot be edited here (status: ${status || 'unknown'}).`
              : 'Event not found.'
          );
        }
      } catch (error: any) {
        console.error('Error loading event data:', error);
        setError(error?.response?.data?.error || 'Failed to load event data');
      } finally {
        setIsLoadingEventData(false);
      }
    };

    const getCountriesAndCategories = async () => {
      try {
        const response = await getAllCountries();
        console.log("getAllCountries response", response);
        setCountries(response);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
      try {
        const response = await getAllCategories();
        console.log("getAllCategories response", response);
        setCategories(response);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    
    loadEventData();
    getCountriesAndCategories();
  }, [eventId]);


  // Handle event type selection
  const handleEventTypeSelect = (isOnlineEvent: boolean) => {
    handleInputChange('isOnlineEvent', isOnlineEvent);
    setEventTypeSelected(true);
  };

  // Handle file upload
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    handleInputChange('eventImage', file);
  };

  // Initialize validation on component mount
  React.useEffect(() => {
  updateValidation(formData);
}, [formData]);

  // Get current tab validation status
  const getCurrentTabValidation = (): boolean => {
    switch (activeTab) {
      case 1:
        return tabValidation.detailsTab;
      case 2:
        return tabValidation.ticketsTab;
      case 3:
        return tabValidation.settingsTab;
      case 4:
        return tabValidation.otherDetailsTab;
      default:
        return false;
    }
  };

  // Validation functions
  const validateDetailsTab = (data: EventFormData): boolean => {
    const requiredFields = [
      data.eventName,
      data.eventDate,
      data.eventTime,
      data.eventImage,
      data.eventDescription,
    ];

    if (!data.isSingleDayEvent) {
      requiredFields.push(data.endDate || '');
      requiredFields.push(data.eventEndTime);
    }

    // Check if venue information is complete (only for offline events)
    let venueValid = true;
    if (!data.isOnlineEvent) {
      if (data.hasMultipleVenues) {
        venueValid = data.venues.length > 1 && data.venues.every(venue => 
          venue.venueName && venue.venueAddress 
        );
      } else {
        venueValid = !!(data.venueDetails.name && data.venueDetails.addressLine1 && data.venueDetails.country);
      }
    }
    const requiredFieldsStatus=requiredFields.every(field => field && field.toString().trim() !== '')
    console.log('requiredFieldsStatus', requiredFieldsStatus);
    console.log('venueValid', venueValid);
    return requiredFieldsStatus && venueValid;
  };

const validateTicketsTab = (data: EventFormData): boolean => {
  const rawTickets = Array.isArray(data.tickets) ? data.tickets : [];

  // normalize a display name the same way as the loader
  const tickets = rawTickets
    .map((t: any) => ({
      ...t,
      name: (t?.ticketType ?? t?.name ?? t?.ticketName ?? t?.title ?? t?.type ?? '')
        .toString()
        .trim(),
    }))
    .filter(t => !!t.name);

  // Allow free events without tickets (adjust if your biz rule differs)
  if (data.isTicketFree && tickets.length === 0) return true;

  // Non-free: at least one valid ticket
  if (tickets.length === 0) return false;

  // If grouped, require at least one group row
  if (data.hasMultipleTicketGroups) {
    if (!Array.isArray(data.ticketGroups) || data.ticketGroups.length === 0) return false;
  }

  return true;
};


  const validateSettingsTab = (data: EventFormData): boolean => {
    if(data.templateType===''){
      return false;
    }
    // Basic settings validation - can be expanded based on requirements
    return true; // Settings tab is usually optional
  };

  // Update validation when form data changes
  const updateValidation = (data: EventFormData) => {
    console.log('data', data);
    console.log('validateDetailsTab(data)', validateDetailsTab(data));
    console.log('validateTicketsTab(data)', validateTicketsTab(data));
    console.log('validateSettingsTab(data)', validateSettingsTab(data));
    setTabValidation({
      detailsTab: validateDetailsTab(data),
      ticketsTab: validateTicketsTab(data),
      settingsTab: validateSettingsTab(data),
      otherDetailsTab: true // Optional tab, always valid
    });
  };

const handleInputChange = <K extends keyof EventFormData>(
  field: K,
  valueOrUpdater: EventFormData[K] | ((prevFieldValue: EventFormData[K]) => EventFormData[K])
) => {
  setFormData(prev => {
    let nextFieldValue: EventFormData[K];
    if (typeof valueOrUpdater === 'function') {
      nextFieldValue = (valueOrUpdater as (v: EventFormData[K]) => EventFormData[K])(prev[field]);
    } else {
      nextFieldValue = valueOrUpdater;
    }
    if (field === 'tickets' || field === 'ticketGroups') {
      nextFieldValue = Array.isArray(nextFieldValue)
        ? ([...nextFieldValue] as EventFormData[K])
        : nextFieldValue;
    }
    return {
      ...prev,
      [field]: nextFieldValue,
    };
  });
};


// keep validation in one place so it always runs on the latest data
React.useEffect(() => {
  updateValidation(formData);
}, [formData]);


  // Navigation functions
  const nextTab = () => {
    if (activeTab < 4) {
      setActiveTab(activeTab + 1);
      // Scroll to top of page after tab change
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  const prevTab = () => {
    if (activeTab > 1) {
      setActiveTab(activeTab - 1);
      // Scroll to top of page after tab change
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };
  const canAccessTab = (targetTab: number) => {
      if (targetTab <= activeTab) return true; 
      if (targetTab > 1 && !tabValidation.detailsTab) return false;
      if (targetTab > 2 && !tabValidation.ticketsTab) return false;
      if (targetTab > 3 && !tabValidation.settingsTab) return false;
  
      return true;
    };
  
    const goToTab = (targetTab: number, e?: React.MouseEvent) => {
      e?.preventDefault();
  
      if (canAccessTab(targetTab)) {
        setError('');
        setActiveTab(targetTab);
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
        return;
      }
      if (!tabValidation.detailsTab) setError('Please complete the Details tab before proceeding.');
      else if (!tabValidation.ticketsTab) setError('Please complete the Tickets tab before proceeding.');
      else if (!tabValidation.settingsTab) setError('Please complete the Settings tab before proceeding.');
      else setError('Please complete previous steps before accessing this tab.');
  
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    };

  const finishEvent = async () => {
  try {
    setIsLoading(true);

    const fd = new FormData();
    console.log('Submitting formData:', formData);

    const venueDetails = formData.hasMultipleVenues && formData.venues.length > 0
      ? formData.venues[0]
      : formData.venueDetails;

    // countryId
    const selectedCountry = countries.find(c =>
      c.countryName.toLowerCase().includes(venueDetails?.country?.toLowerCase() || '')
    );
    fd.append('countryId', selectedCountry?.countryId?.toString() || '7');

    // primitive fields
    fd.append('eventName', formData.eventName || '');
    fd.append('eventDate', formData.eventDate || '');
    fd.append('endDate', formData.isSingleDayEvent ? (formData.eventDate || '') : (formData.endDate || formData.eventDate || ''));
    fd.append('eventTime', formData.eventTime || '');
    fd.append('eventEndTime', formData.eventEndTime || '');
    fd.append('isSingleDayEvent', String(formData.isSingleDayEvent));
    fd.append('isSameDayEvent', String(formData.isSingleDayEvent));
    fd.append('duration', formData.duration || '');
    fd.append('eventDescription', formData.eventDescription || '');
    fd.append('shortDescription', formData.shortDescription || '');
    if (!formData.hasMultipleVenues) {
      const locationName = formData.venue || venueDetails?.name || "";
      const locationAddress = formData.eventLocation || venueDetails?.addressLine1 || locationName;
      
      fd.append('eventLocation', locationAddress);
      fd.append('venue', locationName);
      fd.append('eventAddress1', locationAddress);
    }
    fd.append('isOnlineEvent', String(formData.isOnlineEvent));
    fd.append('hasMultipleVenues', String(formData.hasMultipleVenues));
    fd.append('hasMultipleTicketGroups', String(formData.hasMultipleTicketGroups));
    fd.append('isTicketFree', String(formData.isTicketFree));
    fd.append('absorbFees', String(formData.absorbFees)); // backend converts "true"->organizer, "false"->attendee
    fd.append('showOtherFees', formData.absorbFees ? String(false) : String(formData.showOtherFees));
    fd.append('isOtherAttendeeRequired', String(formData.isOtherAttendeeRequired));
    fd.append('hasTermsAndConditions', String(formData.hasTermsAndConditions));
    fd.append('termsAndConditions', formData.termsAndConditions || '');
    fd.append('templateType', formData.templateType || '');
    const selectedCategoryId = formData.categories?.[0] || '';
    fd.append('categoryId', selectedCategoryId);
    const selectedCategoryObj = categories.find(cat => String(cat.categoryId) === selectedCategoryId);
    if (selectedCategoryObj) {
      fd.append('Category', JSON.stringify({
        categoryId: String(selectedCategoryObj.categoryId),
        categoryName: selectedCategoryObj.categoryName
      }));
    }
    fd.append('categories', JSON.stringify(formData.categories || []));
    fd.append('paymentChannel', 'our');
    fd.append('platformFee', '1');

    // hero image (only if new file chosen)
    if (formData.eventImage instanceof File) {
      fd.append('eventImage', formData.eventImage, formData.eventImage.name);
    }

    // complex objects/arrays
    // Always send these as valid JSON strings to prevent backend parsing errors
    fd.append('venueDetails', JSON.stringify(!formData.hasMultipleVenues ? (venueDetails || {}) : {}));

    const locationData = formData.hasMultipleVenues ? formData.venues.map(venue => ({
      name: venue?.name || '',
      venueName: venue?.name || '',
      addressLine1: venue?.addressLine1 || venue?.formattedAddress || '',
      addressLine2: venue?.addressLine2 || '',
      city: venue?.city || '',
      state: venue?.state || '',
      country: venue?.country || '',
      zipCode: venue?.zipCode || '',
      latitude: venue?.latitude,
      longitude: venue?.longitude,
      formattedAddress: venue?.formattedAddress || venue?.addressLine1 || venue?.name || '',
    })) : [{
      name: formData.venue || venueDetails?.name || "",
      venueName: venueDetails?.venueName || "",
      addressLine1: formData.eventLocation || venueDetails?.addressLine1 || "",
      addressLine2: venueDetails?.addressLine2 || "",
      city: venueDetails?.city || "",
      state: venueDetails?.state || "",
      country: venueDetails?.country || "",
      zipCode: venueDetails?.zipCode || "",
      latitude: venueDetails?.latitude || 0,
      longitude: venueDetails?.longitude || 0,
      formattedAddress: formData.eventLocation || venueDetails?.formattedAddress || venueDetails?.addressLine1 || venueDetails?.name || ""
    }];
    fd.append('locationData', JSON.stringify(locationData));

    fd.append('venues', JSON.stringify(formData.hasMultipleVenues ? (formData.venues || []) : []));
    fd.append('tickets', JSON.stringify(formData.tickets || []));
    fd.append('ticketGroups', JSON.stringify(formData.ticketGroups || []));

// ---------- AGENDA ----------
let agendaImageIndex = 0;
const agendaPayload = (formData.agenda || []).map((a, i) => {
  const row: any = { ...a };

  if (a.hostImage instanceof File) {
    // Append actual file under the same field name
    fd.append("hostImage", a.hostImage, a.hostImage.name);

    // Add metadata so backend can match
    row.hostImageKey = a.hostImage.name;
    row.hostImageIndex = agendaImageIndex++;
  }

  // Don’t send raw file/blob URL in JSON
  delete row.hostImage;
  delete row.hostImageUrl;

  return row;
});
fd.append("agenda", JSON.stringify(agendaPayload));

// ---------- LINEUP ----------
let lineupImageIndex = 0;
const lineupPayload = (formData.lineup || []).map((l, i) => {
  const row: any = { ...l };

  if (l.artistImage instanceof File) {
    fd.append("artistImage", l.artistImage, l.artistImage.name);
    row.artistImageKey = l.artistImage.name;
    row.artistImageIndex = lineupImageIndex++;
  }

  delete row.artistImage;
  delete row.artistImageUrl;

  return row;
});
fd.append("lineup", JSON.stringify(lineupPayload));

// ---------- SPONSORS ----------
let sponsorImageIndex = 0;
const sponsorsPayload = (formData.sponsors || []).map((s, i) => {
  const row: any = { ...s };

  if (s.thumb instanceof File) {
    fd.append("thumb", s.thumb, s.thumb.name);
    row.thumbImageKey = s.thumb.name;
    row.thumbImageIndex = sponsorImageIndex++;
  }

  delete row.thumb;
  delete row.thumbImageUrl;

  return row;
});
fd.append("sponsors", JSON.stringify(sponsorsPayload));


    // ---- UPDATE VIA SLUG ----
    if (!slugId) throw new Error('Missing slugId for update');
    const resp = await updateEventBySlug(slugId, fd);

    // server returns { success, data: { event, ... } }
    const updatedSlug = resp?.data?.event?.slugId || slugId;
    
    navigate(`/events/${updatedSlug}/preview`);
  } catch (error: any) {
    setError(error?.response?.data?.error || error?.message || 'Something went wrong');
    console.error('Error updating event:', error);
  } finally {
    setIsLoading(false);
  }
};


  // Show loading state while fetching event data
  if (isLoadingEventData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <Spinner size="lg" color="primary" />
          <p className="mt-3">Loading event data...</p>
        </div>
      </div>
    );
  }

  // Edit route with a failed load — never show the Create New Event chooser
  if (eventId && !eventTypeSelected) {
    return (
      <>
        <Background />
        <div className="create-event-container create-event-v2-page">
          <div className="container" style={{ maxWidth: 720, margin: '0 auto', paddingTop: 48 }}>
            <PageHeader
              title="Edit Event Studio"
              subtitle="Could not open this event for editing."
              icon={AlertTriangle}
            />
            <div className="alert alert-danger" role="alert">
              {error || 'Event not found or not available for editing.'}
            </div>
            <button
              type="button"
              className="v3-btn-secondary"
              onClick={() => navigate(eventId ? `/events/${eventId}/preview` : '/events')}
            >
              <ChevronLeft size={14} className="me-1" /> Back to preview
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Background />
      {!eventTypeSelected ? (
        // Create flow only (no :eventId) — pick online vs venue first
        <EventTypeSelection onEventTypeSelect={handleEventTypeSelect} />
      ) : (
        // Show main event creation form after event type is selected
        <div className="create-event-container create-event-v2-page">
          <div className="container" style={{ maxWidth: 1200, margin: '0 auto' }}>
            <PageHeader
              title="Edit Event Studio"
              subtitle="Manage event details, ticket tiers, and configurations."
              icon={Sparkles}
            />
            {/* Main Content */}
            <div className="event-dt-block">
              <div className="row justify-content-center">
                <div className="col-xl-12 col-lg-12 col-md-12">
                  <div className="wizard-steps-block">
                    <div className="step-app">
                      {/* Tab Navigation */}
                      <div style={{ display: "flex", overflowX: "auto", borderBottom: "1px solid rgba(255,255,255,0.06)", paddingBottom: 1, gap: 12, marginBottom: 24 }}>
                        {[1, 2, 3, 4].map(i => {
                          const label = i === 1 ? '1. Details & Venues' : i === 2 ? '2. Ticket Architect' : i === 3 ? '3. Advanced Controls' : '4. Other Details';
                          const isActive = activeTab === i;
                          const accessible = canAccessTab(i) && !isLoading;

                          return (
                            <button
                              key={i}
                              type="button"
                              className={`v3-sub-tab ${isActive ? 'active' : ''}`}
                              onClick={(e) => goToTab(i, e)}
                              disabled={!accessible}
                              style={{
                                opacity: !accessible ? 0.5 : 1,
                                cursor: !accessible ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>

                      {/* Tab Content */}
                      <div className="step-content">
                        {/* Details Tab */}
                        {activeTab === 1 && (
                          <EventDetailsTab 
                            formData={formData}
                            onInputChange={handleInputChange}
                            onImageUpload={handleImageUpload}
                            activeTab={activeTab}
                          />
                        )}

                        {/* Tickets Tab */}
                        {activeTab === 2 && (
                          <EventTicketsTab 
                            tickets={formData.tickets}
                            onInputChange={handleInputChange}
                            activeTab={activeTab}
                            hasMultipleVenues={formData.hasMultipleVenues}
                            venues={formData.venues}
                            hasMultipleTicketGroups={formData.hasMultipleTicketGroups}
                            ticketGroups={formData.ticketGroups}
                            isTicketFree={formData.isTicketFree}
                            absorbFees={formData.absorbFees as boolean}
                           
                          />
                        )}

                        {/* Settings Tab */}
                        {activeTab === 3 && (
                          <EventSettingsTab 
                            formData={formData}
                            onInputChange={handleInputChange}
                            activeTab={activeTab}
                          />
                        )}

                        {/* Other Details Tab */}
                        {activeTab === 4 && (
                          <EventOtherDetailsTab 
                            formData={formData}
                            onInputChange={handleInputChange}
                            activeTab={activeTab}
                          />
                        )}
                      </div>

                      {/* Validation Message */}
                      {!getCurrentTabValidation() && (
                        <div
                          style={{
                            padding: "12px 16px",
                            borderRadius: 12,
                            background: "rgba(255, 171, 0, 0.1)",
                            border: "1px solid rgba(255, 171, 0, 0.25)",
                            color: "#FFC107",
                            fontSize: 12,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            margin: "20px 0 0 0",
                          }}
                        >
                          <AlertTriangle size={16} color="#FFC107" />
                          <span>Please complete all required fields before proceeding to the next step.</span>
                        </div>
                      )}

                      {/* Navigation Buttons */}
                      <div
                        className="glass-panel"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginTop: 20,
                          padding: "16px 20px",
                        }}
                      >
                        <div>
                          {activeTab > 1 && (
                            <button
                              type="button"
                              className="v3-btn-secondary"
                              onClick={prevTab}
                              disabled={isLoading}
                            >
                              <ChevronLeft size={16} /> Previous
                            </button>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 12 }}>
                          {activeTab < 4 && (
                            <button
                              type="button"
                              className="v3-btn-primary"
                              onClick={nextTab}
                              disabled={!getCurrentTabValidation() || isLoading}
                              style={{
                                opacity: (!getCurrentTabValidation() || isLoading) ? 0.5 : 1,
                                cursor: (!getCurrentTabValidation() || isLoading) ? "not-allowed" : "pointer",
                              }}
                            >
                              Next <ChevronRight size={16} />
                            </button>
                          )}
                          {activeTab === 4 && (
                            <button
                              type="button"
                              className="v3-btn-primary"
                              onClick={finishEvent}
                              disabled={isLoading}
                            >
                              {isLoading ? <Spinner size="sm" color="white" /> : 'Update Event'} <Sparkles size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                      {error && (
                        <div className="alert alert-danger" role="alert">
                          {error}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditQuickEventV2;